import moment, {
  Moment,
  MomentInput,
  DurationInputArg1,
  DurationInputArg2,
  unitOfTime,
} from 'moment';
import { EventValue } from 'rc-picker/lib/interface';

import { DateParams } from 'utils/filterUtils';

export const TIME_PERIODS = ['AM', 'PM'];

export const TIME_OPTIONS = [
  '1:00',
  '1:30',
  '2:00',
  '2:30',
  '3:00',
  '3:30',
  '4:00',
  '4:30',
  '5:00',
  '5:30',
  '6:00',
  '6:30',
  '7:00',
  '7:30',
  '8:00',
  '8:30',
  '9:00',
  '9:30',
  '10:00',
  '10:30',
  '11:00',
  '11:30',
  '12:00',
  '12:30',
];

export const getFormattedUTCDate = (
  date: Date,
  format = 'h:mm A [UTC] MM/DD/YYYY',
) => moment(date).utc().format(format);

export const disablePastDates = (current: any) => {
  // Can not select days before today
  return current && current < moment().subtract(1, 'day').endOf('day');
};

export const disableDatesExceptSundays = (date: Moment) => {
  return moment(date).day() !== 0;
};

export const disableDatesBeforeSpecificDate =
  (specificDate: any) => (current: any) => {
    // Cannot select days before specificDate
    // moment(null) gives invalid date
    const correctSpecificDate = specificDate || undefined;

    return (
      current &&
      current < moment(correctSpecificDate).subtract(1, 'day').endOf('day')
    );
  };

export const getLaterDate = (data: string | null, daysToAdd?: number) => {
  const futureDate = moment.utc().add(daysToAdd, 'days');
  const isInputDateAfter = moment.utc(data).isSameOrAfter(futureDate);

  return isInputDateAfter ? data : futureDate;
};

export const disableDatesExceptDatesRange = (
  current: Moment,
  startDate?: Moment | EventValue<Moment>,
  endDate?: Moment | EventValue<Moment>,
  amount: number = 1,
  unitOfTime: unitOfTime.Diff = 'day',
) => {
  const tooLate = startDate && current.diff(startDate, unitOfTime) > amount;
  const tooEarly = endDate && endDate.diff(current, unitOfTime) > amount;

  return tooEarly || tooLate;
};

export const isTodayDate = (date: Date | string | null) => {
  if (!date) return null;

  return moment(date).isSame(new Date(), 'd');
};

export const combineDateTime = (
  date?: Date | string | null,
  time?: Date | string | null,
) => {
  const formattedDate = moment.utc(date).format('MM/DD/YYYY');

  return moment.utc(`${formattedDate} ${time}`);
};

export const getDaysDiffWithSpecificDate = (date: Date | string) => {
  return moment(date).diff(new Date(), 'days');
};

export const getDateFromNow = (date: Date | string) => {
  // For instant entity creation server sends time which is more than current browser time
  // https://mybasepay.atlassian.net/browse/MBO-1486
  const isServerTimeMoreThanCurrent = moment(date).diff(new Date()) > 0;
  const correctDate = isServerTimeMoreThanCurrent ? new Date() : date;

  return moment(correctDate).fromNow();
};

export const getDateWithoutTime = (date?: MomentInput) => {
  return moment.utc(moment(date).format('MM/DD/YYYY')).toDate();
};

export const getLastSunday = () => {
  return moment().startOf('week');
};

export const getPreviousDate = (
  date: Date | string | Moment,
  step: DurationInputArg1 = 1,
  unit: DurationInputArg2 = 'week',
) => {
  return moment(date).subtract(step, unit);
};

export const getNextDate = (
  date: Date | string | Moment,
  step: DurationInputArg1 = 1,
  unit: DurationInputArg2 = 'week',
) => {
  return moment(date).add(step, unit);
};

interface DatesRangeOptions {
  startDate: Date | string | Moment;
  endDate: Date | string | Moment;
  step?: DurationInputArg1;
  unit?: DurationInputArg2;
}

export const getDatesRange = (options: DatesRangeOptions) => {
  const { step = 1, unit = 'week' } = options;
  const startDate = moment(options.startDate);
  const endDate = moment(options.endDate);
  let current = startDate.clone();
  const datesRange: Moment[] = [];

  do {
    datesRange.push(current);
    current = getNextDate(current, step, unit);
  } while (current.isSameOrBefore(endDate));

  return datesRange;
};

export const getDateRangeTillLastSunday = (
  step: DurationInputArg1 = 1,
  unit: DurationInputArg2 = 'week',
): DateParams => {
  const lastSunday = getLastSunday();
  const startDate = getPreviousDate(lastSunday, step, unit);

  return {
    startDate: moment(startDate, 'MM/DD/YYYY'),
    endDate: moment(lastSunday, 'MM/DD/YYYY'),
  };
};

export const disableSpecificNumberDates =
  (
    startDate?: EventValue<Moment>,
    endDate?: EventValue<Moment>,
    amount: number = 1,
  ) =>
  (current: Moment) => {
    if (!startDate && !endDate) {
      return disableDatesExceptSundays(current);
    }

    return (
      disableDatesExceptDatesRange(
        current,
        startDate,
        endDate,
        amount,
        'weeks',
      ) || disableDatesExceptSundays(current)
    );
  };
